import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../shared/ui/toastStore'
import {
  getCatalogCategories,
  updatePartCard,
  type PartCardIdentity,
} from '../../shared/api/catalog.api'

// Инлайн-форма правки карточки парта: бренд / полное наименование / сегмент.
// Правка «локальная»: существующие заявки не меняются; для НОВЫХ заявок
// правка авторитетна (сегмент — приоритет распознавания, бренд — приоритет
// автоподбора парсером). Доступна ADMIN и ROP (гейт в PartCardPage).
export function PartIdentityEditForm({
  identity,
  onClose,
}: {
  identity: PartCardIdentity
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [brand, setBrand] = useState(identity.catalogBrand ?? '')
  const [name, setName] = useState(identity.name ?? '')
  const [category, setCategory] = useState(identity.category ?? '')

  const { data: categories = [] } = useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: getCatalogCategories,
    staleTime: 60_000,
  })

  const initialBrand = identity.catalogBrand ?? ''
  const initialName = identity.name ?? ''

  const saveMut = useMutation({
    mutationFn: () =>
      updatePartCard({
        artKey: identity.artKey,
        partNumber: identity.article,
        // Сегмент необязателен: парты из заявок живут в каталоге без него,
        // пустое значение = «— Без сегмента».
        category: category.trim(),
        // Не менялось → не отправляем поле, бэк не трогает текущее значение.
        // Пустая строка = сброс оверрайда.
        ...(name.trim() !== initialName.trim() ? { name: name.trim() } : {}),
        ...(brand.trim() !== initialBrand.trim() ? { brand: brand.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalog'] })
      toast.success('Карточка парта обновлена — новые заявки подтянут эти данные')
      onClose()
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось сохранить карточку парта'
      toast.error(msg)
    },
  })

  const canSave = !saveMut.isPending

  return (
    <div className="mt-3 rounded-xl border border-brand-200 bg-brand-50/40 p-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-xs text-gray-500">Бренд</span>
          <input
            type="text"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="например: YOUTAI"
            className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Наименование</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="полное наименование"
            className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Сегмент</span>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            list="part-card-segments"
            placeholder="— без сегмента"
            className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <datalist id="part-card-segments">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
          Правка не меняет существующие заявки — только каталог и новые заявки.
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saveMut.isPending}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => saveMut.mutate()}
            disabled={!canSave}
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {saveMut.isPending ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
